import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Edit2,
    Eye,
    FileText,
    Plus,
    RefreshCcw,
    Search,
    Trash2,
} from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import { deletePost, fetchAdminPosts } from "../../../services/api";
import styles from "./PainelBlog.module.css";

const STATUS_LABELS = {
    published: "Publicado",
    draft: "Rascunho",
    archived: "Arquivado",
};

function formatDate(value) {
    if (!value) return "-";

    const normalized = value.endsWith("Z")
        ? value
        : `${value.replace("+00:00", "")}Z`;

    const date = new Date(normalized);

    if (Number.isNaN(date.getTime())) return value;

    return new Intl.DateTimeFormat("pt-BR", {
        dateStyle: "short",
        timeStyle: "short",
    }).format(date);
}

function normalizeText(value = "") {
    return value
        .toString()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim();
}

function getStatusLabel(status) {
    return STATUS_LABELS[status] || "Rascunho";
}

function getStatusClass(status) {
    if (status === "published") return styles.statusPublished;
    if (status === "archived") return styles.statusArchived;
    return styles.statusDraft;
}

function PainelBlogStats({ posts }) {
    const stats = useMemo(() => {
        return posts.reduce(
            (acc, post) => {
                acc.total += 1;

                if (post.status === "published") acc.published += 1;
                else if (post.status === "archived") acc.archived += 1;
                else acc.draft += 1;

                return acc;
            },
            {
                total: 0,
                published: 0,
                draft: 0,
                archived: 0,
            }
        );
    }, [posts]);

    return (
        <div className={styles.statsGrid}>
            <div className={styles.statCard}>
                <span>Total</span>
                <strong>{stats.total}</strong>
            </div>

            <div className={styles.statCard}>
                <span>Publicados</span>
                <strong>{stats.published}</strong>
            </div>

            <div className={styles.statCard}>
                <span>Rascunhos</span>
                <strong>{stats.draft}</strong>
            </div>

            <div className={styles.statCard}>
                <span>Arquivados</span>
                <strong>{stats.archived}</strong>
            </div>
        </div>
    );
}

function EmptyState({ title, description, actionLabel, onAction }) {
    return (
        <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>
                <FileText size={26} aria-hidden="true" />
            </div>

            <h2>{title}</h2>
            <p>{description}</p>

            {actionLabel && onAction && (
                <button type="button" onClick={onAction}>
                    {actionLabel}
                </button>
            )}
        </div>
    );
}

export default function PainelBlog() {
    const { password } = useAuth();
    const navigate = useNavigate();

    const [posts, setPosts] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [error, setError] = useState("");

    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("todos");

    useEffect(() => {
        let isMounted = true;

        async function loadPosts() {
            try {
                setIsLoading(true);
                setError("");

                const data = await fetchAdminPosts(password);

                if (!isMounted) return;

                setPosts(Array.isArray(data?.posts) ? data.posts : []);
            } catch (err) {
                if (!isMounted) return;

                setError(err?.message || "Não foi possível carregar os posts.");
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        }

        loadPosts();

        return () => {
            isMounted = false;
        };
    }, [password]);

    const filteredPosts = useMemo(() => {
        const normalizedSearch = normalizeText(search);

        return posts.filter((post) => {
            const matchesStatus =
                statusFilter === "todos" || post.status === statusFilter;

            const searchableContent = normalizeText(
                `${post.title || ""} ${post.category || ""} ${post.slug || ""}`
            );

            const matchesSearch =
                !normalizedSearch ||
                searchableContent.includes(normalizedSearch);

            return matchesStatus && matchesSearch;
        });
    }, [posts, search, statusFilter]);

    const hasFilters = search || statusFilter !== "todos";

    const handleClearFilters = () => {
        setSearch("");
        setStatusFilter("todos");
    };

    const handleRefresh = async () => {
        try {
            setIsLoading(true);
            setError("");

            const data = await fetchAdminPosts(password);
            setPosts(Array.isArray(data?.posts) ? data.posts : []);
        } catch (err) {
            setError(err?.message || "Não foi possível atualizar os posts.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async (post) => {
        if (confirmDelete !== post.id) {
            setConfirmDelete(post.id);

            setTimeout(() => {
                setConfirmDelete((current) =>
                    current === post.id ? null : current
                );
            }, 3000);

            return;
        }

        try {
            setIsDeleting(post.id);
            setError("");

            await deletePost(password, post.id);

            setPosts((current) =>
                current.filter((item) => item.id !== post.id)
            );

            setConfirmDelete(null);
        } catch (err) {
            setError(err?.message || "Não foi possível deletar o post.");
        } finally {
            setIsDeleting(null);
        }
    };

    return (
        <div className={styles.page}>
            <header className={styles.topbar}>
                <div>
                    <span className={styles.eyebrow}>Painel administrativo</span>
                    <h1>Gerenciar Blog</h1>
                    <p>
                        Crie, edite e organize os conteúdos publicados no blog da
                        HL Software.
                    </p>
                </div>

                <div className={styles.topbarActions}>
                    <button
                        type="button"
                        className={styles.secondaryButton}
                        onClick={handleRefresh}
                        disabled={isLoading}
                    >
                        <RefreshCcw size={17} aria-hidden="true" />
                        Atualizar
                    </button>

                    <button
                        type="button"
                        className={styles.newButton}
                        onClick={() => navigate("/painel/blog/novo")}
                    >
                        <Plus size={18} aria-hidden="true" />
                        Novo post
                    </button>
                </div>
            </header>

            <PainelBlogStats posts={posts} />

            {error && (
                <div className={styles.errorMessage}>
                    <strong>Ops!</strong>
                    <span>{error}</span>
                </div>
            )}

            <section className={styles.filtersCard}>
                <div className={styles.searchField}>
                    <label htmlFor="blog-search">Buscar post</label>

                    <div className={styles.inputWithIcon}>
                        <Search size={17} aria-hidden="true" />
                        <input
                            id="blog-search"
                            type="search"
                            placeholder="Busque por título, categoria ou slug..."
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                        />
                    </div>
                </div>

                <div className={styles.selectField}>
                    <label htmlFor="blog-status">Status</label>

                    <select
                        id="blog-status"
                        value={statusFilter}
                        onChange={(event) => setStatusFilter(event.target.value)}
                    >
                        <option value="todos">Todos os status</option>
                        <option value="published">Publicado</option>
                        <option value="draft">Rascunho</option>
                        <option value="archived">Arquivado</option>
                    </select>
                </div>

                {hasFilters && (
                    <button
                        type="button"
                        className={styles.clearButton}
                        onClick={handleClearFilters}
                    >
                        Limpar filtros
                    </button>
                )}
            </section>

            <section className={styles.listHeader}>
                <div>
                    <strong>{filteredPosts.length}</strong>
                    <span>
                        {filteredPosts.length === 1
                            ? " post encontrado"
                            : " posts encontrados"}
                    </span>
                </div>
            </section>

            <div className={styles.tableWrap}>
                <table className={styles.table}>
                    <thead>
                    <tr>
                        <th>Título</th>
                        <th>Categoria</th>
                        <th>Status</th>
                        <th>Views</th>
                        <th>Atualizado em</th>
                        <th>Ações</th>
                    </tr>
                    </thead>

                    <tbody>
                    {isLoading && (
                        <tr>
                            <td colSpan={6} className={styles.empty}>
                                Carregando posts...
                            </td>
                        </tr>
                    )}

                    {!isLoading && posts.length === 0 && (
                        <tr>
                            <td colSpan={6}>
                                <EmptyState
                                    title="Nenhum post encontrado"
                                    description="Crie o primeiro conteúdo do blog para começar a alimentar a presença digital da HL Software."
                                    actionLabel="Criar primeiro post"
                                    onAction={() =>
                                        navigate("/painel/blog/novo")
                                    }
                                />
                            </td>
                        </tr>
                    )}

                    {!isLoading &&
                        posts.length > 0 &&
                        filteredPosts.length === 0 && (
                            <tr>
                                <td colSpan={6}>
                                    <EmptyState
                                        title="Nenhum post encontrado para os filtros"
                                        description="Tente alterar a busca ou limpar os filtros para visualizar todos os posts."
                                        actionLabel="Limpar filtros"
                                        onAction={handleClearFilters}
                                    />
                                </td>
                            </tr>
                        )}

                    {!isLoading &&
                        filteredPosts.map((post) => (
                            <tr key={post.id}>
                                <td className={styles.titleCell}>
                                    <strong>{post.title}</strong>

                                    {post.slug && (
                                        <span>/blog/{post.slug}</span>
                                    )}
                                </td>

                                <td>{post.category || "-"}</td>

                                <td>
                                        <span
                                            className={`${styles.statusPill} ${getStatusClass(
                                                post.status
                                            )}`}
                                        >
                                            {getStatusLabel(post.status)}
                                        </span>
                                </td>

                                <td className={styles.viewsCell}>
                                    <Eye size={15} aria-hidden="true" />
                                    {Number(post.view_count || 0).toLocaleString("pt-BR")}
                                </td>

                                <td>{formatDate(post.updated_at)}</td>

                                <td className={styles.actions}>
                                    <button
                                        type="button"
                                        className={styles.editButton}
                                        onClick={() =>
                                            navigate(
                                                `/painel/blog/editar/${post.id}`
                                            )
                                        }
                                        aria-label={`Editar post ${post.title}`}
                                        title="Editar post"
                                    >
                                        <Edit2 size={16} />
                                    </button>

                                    <button
                                        type="button"
                                        className={`${styles.deleteButton} ${
                                            confirmDelete === post.id
                                                ? styles.deleteConfirm
                                                : ""
                                        }`}
                                        onClick={() => handleDelete(post)}
                                        disabled={isDeleting === post.id}
                                        aria-label={`Deletar post ${post.title}`}
                                        title={
                                            confirmDelete === post.id
                                                ? "Clique novamente para confirmar"
                                                : "Deletar post"
                                        }
                                    >
                                        <Trash2 size={16} />

                                        {confirmDelete === post.id && (
                                            <span>
                                                    {isDeleting === post.id
                                                        ? "Deletando..."
                                                        : "Confirmar?"}
                                                </span>
                                        )}
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}